import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, maskCPF } from "../../lib/api";
import { ScoreBadge, StatusBadge } from "../../components/Score";
import { ClientTierBadge } from "../../components/TierBadge";
import { useAuth } from "../../contexts/AuthContext";
import { Search, Plus, Eye, ShoppingBag } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Ativos" },
  { key: "defaulter", label: "Inadimplentes" },
  { key: "blocked", label: "Bloqueados" },
];

export default function ResellerClients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const base = user?.role === "admin" ? "/admin/clientes" : "/revenda/clientes";

  useEffect(() => { (async () => setItems((await api.get("/clients")).data))(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((c) => {
      const matchQ = !term || (c.name || "").toLowerCase().includes(term) || (c.phone || "").includes(term) || (c.cpf || "").includes(term);
      const matchF = filter === "all" || c.status === filter;
      return matchQ && matchF;
    });
  }, [items, q, filter]);

  return (
    <div data-testid="reseller-clients-page" className="space-y-6">
      <div className="topbar">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand">Carteira</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-brand-dark">Minhas Clientes</h1>
        </div>
        <Link to="/revenda/clientes/novo" className="brand" data-testid="cta-new-client"><Plus size={16} /> Novo Cliente</Link>
      </div>

      {/* Search + chips */}
      <div className="space-y-3">
        <div className="relative">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
          <input
            data-testid="client-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, telefone ou CPF..."
            className="h-12 w-full rounded-2xl border border-border bg-white pl-11 pr-4 text-sm shadow-card outline-none transition-shadow focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`client-filter-${f.key}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                filter === f.key ? "bg-brand-grad text-white shadow-[0_4px_12px_rgba(106,27,154,0.28)]" : "border border-border bg-white text-[#7A6A78] hover:bg-muted"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((c) => {
          const avail = Math.max(0, Number(c.credit_limit || 0) - Number(c.open_balance || 0));
          return (
            <div key={c.id} data-testid={`client-row-${c.id}`}
              className="flex flex-col rounded-2xl border border-border bg-white p-5 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-grad text-sm font-semibold text-white">
                    {(c.name || "?")[0]?.toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <Link to={`${base}/${c.id}`} data-testid={`client-name-link-${c.id}`}
                      className="block truncate text-sm font-semibold text-brand-dark hover:underline">{c.name}</Link>
                    <div className="mt-0.5 text-xs tabular text-[#7A6A78]">{maskCPF(c.cpf)}</div>
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </div>

              <div className="mt-4 flex items-center gap-2">
                <ScoreBadge score={c.score} />
                <ClientTierBadge credit_limit={c.credit_limit} compact />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-muted/60 p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#9A8A98]">Limite</div>
                  <div className="text-sm font-semibold tabular text-brand-dark">{brl(c.credit_limit)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#9A8A98]">Disponível</div>
                  <div className="text-sm font-semibold tabular text-brand">{brl(avail)}</div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Link to={`${base}/${c.id}`} data-testid={`client-view-${c.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border bg-white py-2.5 text-xs font-semibold text-brand-dark transition-colors hover:bg-muted">
                  <Eye size={15} /> Visualizar
                </Link>
                <Link to="/revenda/venda/nova" data-testid={`client-sell-${c.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-brand-grad py-2.5 text-xs font-semibold text-white transition-transform active:scale-95">
                  <ShoppingBag size={15} /> Vender
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-white py-14 text-center text-sm text-[#7A6A78]">
          {items.length === 0 ? "Sua carteira está vazia. Cadastre sua primeira cliente." : "Nenhuma cliente encontrada com esses filtros."}
        </div>
      )}
    </div>
  );
}
